import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';

const AI_MAIN_AGENT = {
  id: 'ai-main',
  name: 'AI Main',
  desc: 'Foundation model · Full acquisition authority',
  color: '#6366f1',
  locked: true
};

const AGENTS_BY_TAB = {
  vibe: [
    { id: 'legal',     name: 'Legal Agent',    desc: 'FAR/VAAR compliance · Protest defense',  color: '#ef4444' },
    { id: 'budget',    name: 'Budget Agent',    desc: 'Cost estimation · IGCE development',     color: '#10b981' },
    { id: 'security',  name: 'Security Agent',  desc: 'Clearance-aware · OPSEC screening',      color: '#8b5cf6' },
    { id: 'research',  name: 'Research Agent',  desc: 'Market intelligence · Vendor analysis',  color: '#3b82f6' },
    { id: 'reasoning', name: 'Reasoning Agent', desc: 'Deep analysis · R1 reasoning engine',    color: '#ec4899' },
  ],
  context: [
    { id: 'extractor', name: 'Data Extractor',  desc: 'Captures fields · Builds session data',  color: '#f59e0b' },
    { id: 'reqs',      name: 'Requirements',    desc: 'Requirement ID · Traceability',           color: '#3b82f6' },
    { id: 'tracker',   name: 'Session Tracker', desc: 'Context persistence · Module sync',       color: '#10b981' },
    { id: 'context',   name: 'Context Builder', desc: 'Ambient context layer · Intent mapping',  color: '#8b5cf6' },
  ],
  help: [
    { id: 'farguid',   name: 'FAR Guide',       desc: 'FAR/VAAR citations · Reg lookup',        color: '#f59e0b' },
    { id: 'explainer', name: 'Quick Explainer', desc: 'Plain English · Instant tooltips',        color: '#3b82f6' },
    { id: 'coach',     name: 'Workflow Coach',  desc: 'Step-by-step guidance · Best practices',  color: '#10b981' },
    { id: 'training',  name: 'Training Assist', desc: 'Junior CO support · Learning mode',       color: '#ec4899' },
  ],
  comply: [
    { id: 'farcheck',  name: 'FAR Compliance',  desc: 'FAR Part scan · Citation verification',  color: '#ef4444' },
    { id: 'vaar',      name: 'VAAR Specialist', desc: 'VA-specific regs · VAAR cross-check',     color: '#f59e0b' },
    { id: 'sec508',    name: 'Section 508',     desc: 'Accessibility compliance · IT regs',      color: '#8b5cf6' },
    { id: 'cyber',     name: 'Cybersecurity',   desc: 'FISMA · VA Directive 6500 · ITAR',        color: '#06b6d4' },
  ],
  generate: [
    { id: 'sowdraft',  name: 'SOW Drafter',     desc: 'Statement of Work · Performance-based',  color: '#8b5cf6' },
    { id: 'pwswrite',  name: 'PWS Writer',      desc: 'Performance Work Statement · Metrics',    color: '#3b82f6' },
    { id: 'jagen',     name: 'J&A Generator',   desc: 'Justification & Authorization · FAR 6',  color: '#ef4444' },
    { id: 'igce',      name: 'IGCE Builder',    desc: 'Cost estimate · Price reasonableness',    color: '#10b981' },
  ],
  signature: [],
};

const SLIDER_LABELS = {
  tone:       ['Formal', 'Casual'],
  depth:      ['Quick', 'Thorough'],
  speed:      ['Fast', 'Deep'],
  confidence: ['Suggest', 'Assert']
};

const COLOR_SWATCHES = ['#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6','#f97316','#ec4899','#6366f1'];
const CURSOR_ICONS   = ['B','C','+','?','P','S'];

function useClickOutside(ref, onClose) {
  useEffect(() => {
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [ref, onClose]);
}

function AgentAccordion({ agent, personality, onPersonalityChange, darkMode, border, sub, txt }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderRadius: '8px', border: `1px solid ${border}`, marginBottom: '6px', overflow: 'hidden' }}>
      <div onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 10px', cursor: 'pointer', userSelect: 'none' }}>
        <div style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: agent.color, flexShrink: 0 }} />
        <span style={{ fontSize: '12px', fontWeight: '600', color: txt, flex: 1 }}>{agent.name}</span>
        <span style={{ fontSize: '11px', color: sub }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <div style={{ padding: '4px 10px 10px 10px' }}>
          {Object.entries(SLIDER_LABELS).map(([key, [left, right]]) => (
            <div key={key} style={{ marginBottom: '7px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                <span style={{ fontSize: '10px', color: sub }}>{left}</span>
                <span style={{ fontSize: '10px', color: sub }}>{right}</span>
              </div>
              <input type="range" min="0" max="100" value={personality?.[key] ?? 50}
                onChange={e => onPersonalityChange(agent.id, key, parseInt(e.target.value))}
                style={{ width: '100%', accentColor: agent.color, height: '3px', margin: 0 }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function BotSettingLeftPanel({ slot, slotColor, onSave }) {
  const { darkMode } = useTheme();
  const mode = slot?.mode || 'vibe';
  const availableAgents = AGENTS_BY_TAB[mode] || [];

  const initSpecialists = () => {
    const saved = slot?.agents || [];
    return saved.filter(a => a.id !== 'ai-main' && a.id !== 'va-big-daddy')
      .map(a => a.id).filter(id => availableAgents.find(ag => ag.id === id)).slice(0, 2);
  };

  const [selectedSpecialists, setSelectedSpecialists] = useState(initSpecialists);
  const [selectedColor, setSelectedColor] = useState(slot?.color || slotColor || '#3b82f6');
  const [selectedIcon, setSelectedIcon]   = useState(slot?.cursorIcon || 'B');
  const [shakeId, setShakeId]             = useState(null);

  const initPersonalities = () => {
    const result = {};
    availableAgents.forEach(a => { result[a.id] = { tone: 50, depth: 50, speed: 50, confidence: 50 }; });
    return result;
  };
  const [personalities, setPersonalities] = useState(initPersonalities);

  // OPTIONS state
  const [proactivity,        setProactivity]        = useState(slot?.proactivity        ?? 50);
  const [systemPrompt,       setSystemPrompt]        = useState(slot?.systemPrompt       || '');
  const [systemPromptCustom, setSystemPromptCustom]  = useState(slot?.systemPromptCustom || false);
  const [promptOpen,         setPromptOpen]          = useState(false);
  const [coordinatorPrompt,  setCoordinatorPrompt]   = useState(slot?.coordinatorPrompt  || '');
  const [proactivityOpen,    setProactivityOpen]     = useState(false);

  const [colorOpen, setColorOpen] = useState(false);
  const [iconOpen,  setIconOpen]  = useState(false);
  const colorRef       = useRef(null);
  const iconRef        = useRef(null);
  const proactivityRef = useRef(null);
  useClickOutside(colorRef,       () => setColorOpen(false));
  useClickOutside(iconRef,        () => setIconOpen(false));
  useClickOutside(proactivityRef, () => setProactivityOpen(false));

  const generatePrompt = useCallback(() => {
    return `You are a procurement AI assistant for federal acquisitions.\nContext: always includes the full page and project baseline.\nCoordinate with any specialist agents in your stack.`;
  }, []);

  useEffect(() => {
    if (!systemPromptCustom) setSystemPrompt(generatePrompt());
  }, [systemPromptCustom, generatePrompt]);

  const toggleAgent = (agentId) => {
    setSelectedSpecialists(prev => {
      if (prev.includes(agentId)) return prev.filter(id => id !== agentId);
      if (prev.length >= 2) { setShakeId(agentId); setTimeout(() => setShakeId(null), 500); return prev; }
      return [...prev, agentId];
    });
  };

  const handlePersonalityChange = (agentId, key, value) => {
    setPersonalities(p => ({ ...p, [agentId]: { ...(p[agentId] || {}), [key]: value } }));
  };

  const handleSave = useCallback(() => {
    const specialists = availableAgents.filter(a => selectedSpecialists.includes(a.id));
    onSave({
      ...slot,
      color: selectedColor,
      cursorIcon: selectedIcon,
      agents: [AI_MAIN_AGENT, ...specialists],
      personality: { tone: 50, depth: 50, speed: 50, confidence: 50 },
      agentPersonalities: personalities,
      proactivity,
      systemPrompt,
      systemPromptCustom,
      coordinatorPrompt,
    });
  }, [selectedColor, selectedIcon, selectedSpecialists, personalities, proactivity, systemPrompt, systemPromptCustom, coordinatorPrompt, onSave, slot, availableAgents]);

  const txt     = darkMode ? '#f9fafb' : '#111827';
  const sub     = darkMode ? '#9ca3af' : '#6b7280';
  const bg      = darkMode ? '#1a1f2e' : '#f8f9fa';
  const border  = darkMode ? '#2d3748' : '#e5e7eb';
  const panelBg = darkMode ? '#111827' : '#ffffff';
  const cardBg  = darkMode ? '#1a1f2e' : '#f8f9fa';

  const selectedAgentObjects = availableAgents.filter(a => selectedSpecialists.includes(a.id));
  const sectionLabel = { fontSize: '10px', color: sub, fontWeight: '600', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '8px', display: 'block' };

  const proactivityLabel = proactivity <= 33 ? 'Silent' : proactivity <= 66 ? 'Balanced' : 'Proactive';

  return (
    <div style={{ padding: '16px 20px', boxSizing: 'border-box', overflowY: 'auto', height: '100%' }}>

      {/* ── AGENT GRID ── */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '8px' }}>
          <span style={sectionLabel}>Agents</span>
          <span style={{ fontSize: '10px', color: sub }}>{selectedSpecialists.length}/2 specialists</span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '7px' }}>
          {/* AI Main — full width, locked */}
          <div style={{
            gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: '8px',
            padding: '7px 10px', borderRadius: '8px',
            border: `1.5px solid ${AI_MAIN_AGENT.color}`,
            backgroundColor: `${AI_MAIN_AGENT.color}12`
          }}>
            <div style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: AI_MAIN_AGENT.color, flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <span style={{ fontSize: '11px', fontWeight: '700', color: AI_MAIN_AGENT.color }}>AI Main</span>
              <span style={{ fontSize: '10px', color: sub, marginLeft: '6px' }}>Standard · Always active</span>
            </div>
            <svg width="10" height="10" viewBox="0 0 24 24" fill={AI_MAIN_AGENT.color}>
              <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
            </svg>
          </div>

          {/* Specialist cards */}
          {availableAgents.map(agent => {
            const isSelected = selectedSpecialists.includes(agent.id);
            return (
              <div key={agent.id} onClick={() => toggleAgent(agent.id)} style={{
                borderRadius: '8px', border: `1.5px solid ${isSelected ? agent.color : border}`,
                padding: '7px 9px', backgroundColor: isSelected ? `${agent.color}18` : cardBg,
                cursor: 'pointer', position: 'relative', transition: 'all 0.15s ease',
                animation: shakeId === agent.id ? 'shake 0.4s ease' : 'none'
              }}>
                <div style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: agent.color, marginBottom: '4px' }} />
                <div style={{ fontSize: '11px', fontWeight: '600', color: txt, marginBottom: '2px', lineHeight: 1.2 }}>{agent.name}</div>
                <div style={{ fontSize: '10px', color: sub, lineHeight: 1.3 }}>{agent.desc}</div>
                {isSelected && (
                  <div style={{ position: 'absolute', top: '5px', right: '5px', width: '13px', height: '13px',
                    borderRadius: '50%', backgroundColor: agent.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="7" height="7" viewBox="0 0 24 24" fill="white"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── PERSONALITY (specialists only) ── */}
      {selectedAgentObjects.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <span style={sectionLabel}>Personality</span>
          {selectedAgentObjects.map(agent => (
            <AgentAccordion key={agent.id} agent={agent} personality={personalities[agent.id]}
              onPersonalityChange={handlePersonalityChange} darkMode={darkMode} border={border} sub={sub} txt={txt} />
          ))}
        </div>
      )}

      {/* ── OPTIONS ── */}
      <div style={{ marginBottom: '16px' }}>
        <span style={sectionLabel}>Options</span>

        {/* System Prompt */}
        <div style={{ marginBottom: '10px' }}>
          <div onClick={() => setPromptOpen(o => !o)} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '6px 10px', borderRadius: '6px', cursor: 'pointer',
            backgroundColor: darkMode ? '#1a1f2e' : '#f3f4f6', border: `1px solid ${border}`
          }}>
            <span style={{ fontSize: '11px', color: txt, fontWeight: '600' }}>
              {promptOpen ? '▼' : '▶'} System Prompt
            </span>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <span style={{ fontSize: '10px', color: sub }}>{systemPromptCustom ? 'Custom' : 'Auto'}</span>
              {systemPromptCustom && (
                <span onClick={e => { e.stopPropagation(); setSystemPromptCustom(false); setSystemPrompt(generatePrompt()); }}
                  style={{ fontSize: '10px', color: '#3b82f6', cursor: 'pointer' }}>Reset</span>
              )}
            </div>
          </div>
          {promptOpen && (
            <textarea value={systemPrompt}
              onChange={e => { setSystemPrompt(e.target.value); setSystemPromptCustom(true); }}
              style={{ width: '100%', height: '80px', marginTop: '6px', padding: '8px', fontSize: '11px',
                borderRadius: '6px', border: `1px solid ${border}`, backgroundColor: panelBg,
                color: txt, outline: 'none', resize: 'vertical', boxSizing: 'border-box',
                fontFamily: 'monospace', lineHeight: 1.4 }}
            />
          )}
        </div>

        {/* Coordinator Prompt — only when 2 specialists */}
        {selectedSpecialists.length >= 2 && (
          <div style={{ marginBottom: '10px' }}>
            <div style={{ fontSize: '11px', color: sub, marginBottom: '5px' }}>Coordinator Prompt</div>
            <textarea value={coordinatorPrompt} onChange={e => setCoordinatorPrompt(e.target.value)}
              placeholder="When agents disagree, defer to Legal on compliance, Budget on cost. Synthesize into one response unless conflict is material — then flag both."
              style={{ width: '100%', height: '52px', padding: '7px', fontSize: '11px',
                borderRadius: '6px', border: `1px solid ${border}`, backgroundColor: panelBg,
                color: txt, outline: 'none', resize: 'none', boxSizing: 'border-box', lineHeight: 1.4 }}
            />
          </div>
        )}
      </div>

      {/* ── APPEARANCE + PROACTIVITY + SAVE (compact bottom row) ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px',
        borderRadius: '8px', border: `1px solid ${border}`, backgroundColor: bg }}>

        {/* Color dot */}
        <div ref={colorRef} style={{ position: 'relative' }}>
          <div onClick={() => { setColorOpen(o => !o); setIconOpen(false); setProactivityOpen(false); }}
            style={{ width: '16px', height: '16px', borderRadius: '50%', backgroundColor: selectedColor,
              cursor: 'pointer', border: `2px solid ${border}`, flexShrink: 0 }} title="Slot color" />
          {colorOpen && (
            <div style={{ position: 'absolute', bottom: '24px', left: 0, backgroundColor: panelBg,
              border: `1px solid ${border}`, borderRadius: '8px', padding: '7px', display: 'flex',
              gap: '5px', zIndex: 100, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
              {COLOR_SWATCHES.map(c => (
                <div key={c} onClick={() => { setSelectedColor(c); setColorOpen(false); }}
                  style={{ width: '18px', height: '18px', borderRadius: '50%', backgroundColor: c, cursor: 'pointer',
                    border: selectedColor === c ? '2px solid white' : '2px solid transparent',
                    outline: selectedColor === c ? `2px solid ${c}` : 'none' }} />
              ))}
            </div>
          )}
        </div>

        {/* Cursor icon */}
        <div ref={iconRef} style={{ position: 'relative' }}>
          <div onClick={() => { setIconOpen(o => !o); setColorOpen(false); setProactivityOpen(false); }}
            style={{ width: '22px', height: '18px', borderRadius: '4px',
              backgroundColor: darkMode ? '#2d3748' : '#e5e7eb',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '10px', fontWeight: '700', cursor: 'pointer', color: txt }} title="Cursor icon">
            {selectedIcon}
          </div>
          {iconOpen && (
            <div style={{ position: 'absolute', bottom: '26px', left: 0, backgroundColor: panelBg,
              border: `1px solid ${border}`, borderRadius: '8px', padding: '7px', display: 'flex',
              gap: '5px', zIndex: 100, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
              {CURSOR_ICONS.map(ic => (
                <div key={ic} onClick={() => { setSelectedIcon(ic); setIconOpen(false); }}
                  style={{ width: '26px', height: '24px', borderRadius: '4px', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '700', cursor: 'pointer',
                    backgroundColor: selectedIcon === ic ? selectedColor : (darkMode ? '#2d3748' : '#e5e7eb'),
                    color: selectedIcon === ic ? 'white' : txt }}>
                  {ic}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Divider */}
        <div style={{ width: '1px', height: '14px', backgroundColor: border, flexShrink: 0 }} />

        {/* Proactivity button → popover */}
        <div ref={proactivityRef} style={{ position: 'relative' }}>
          <div onClick={() => { setProactivityOpen(o => !o); setColorOpen(false); setIconOpen(false); }}
            style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '3px 8px',
              borderRadius: '20px', cursor: 'pointer', fontSize: '10px', fontWeight: '600',
              backgroundColor: proactivityOpen ? selectedColor : (darkMode ? '#2d3748' : '#e5e7eb'),
              color: proactivityOpen ? 'white' : sub, border: `1px solid ${border}` }}>
            <span>⚡</span>
            <span>{proactivityLabel}</span>
          </div>
          {proactivityOpen && (
            <div style={{ position: 'absolute', bottom: '30px', left: 0, width: '200px',
              backgroundColor: panelBg, border: `1px solid ${border}`, borderRadius: '10px',
              padding: '12px', zIndex: 100, boxShadow: '0 4px 16px rgba(0,0,0,0.15)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ fontSize: '11px', color: sub }}>Wait to be asked</span>
                <span style={{ fontSize: '11px', color: sub }}>Volunteer info</span>
              </div>
              <input type="range" min="0" max="100" value={proactivity}
                onChange={e => setProactivity(parseInt(e.target.value))}
                style={{ width: '100%', accentColor: selectedColor, height: '3px', margin: 0 }}
              />
              <div style={{ fontSize: '10px', color: sub, marginTop: '6px', textAlign: 'center' }}>
                {proactivity <= 33 ? 'Only responds when asked' :
                 proactivity <= 66 ? 'Suggests when it spots gaps' :
                 'Actively flags issues & next steps'}
              </div>
            </div>
          )}
        </div>

        {/* Save */}
        <button onClick={handleSave} style={{ marginLeft: 'auto', padding: '4px 12px',
          fontSize: '11px', fontWeight: '600', borderRadius: '5px',
          backgroundColor: selectedColor, color: 'white', border: 'none', cursor: 'pointer', flexShrink: 0 }}>
          Save
        </button>
      </div>

      <style>{`
        @keyframes shake {
          0%,100% { transform: translateX(0); }
          20%,60%  { transform: translateX(-4px); }
          40%,80%  { transform: translateX(4px); }
        }
      `}</style>
    </div>
  );
}
